import { sessionDate } from '@/lib/session-labels';
import {
  type NoyauCode,
  type NoyauDetermination,
  type NoyauGapLineState,
  type NoyauTrialSummary,
  type SessionDetail,
  noyauGapLineOf,
  noyauOf,
} from '@matter_extractor/shared';

/**
 * What the head of the parcours SAYS of who played and of the noyau that came
 * out of it (refonte-modulaire.md §12.2 « Depuis #218 », noyau.md §9.2) -
 * pure, the régime of `session-analysis.ts`: the component only draws.
 *
 * ## Nothing here derives a noyau
 *
 * The retained noyau is the one `session.noyauCodes.retained` carries - the
 * VERY field the list reads for its pill - and what retained it is the state
 * `noyauGapLineOf` of `shared` writes for the line of the gap, never a second
 * reading of §3.8. The trial's principal is `session.noyauTrial`, again the
 * list's own field; the trials of the session only lend the notes of that
 * trial, and are not asked for when the line is a retained noyau.
 */

/** The title and the breadcrumb of the head (#218): the person first, the experience after. */
export type HeadTitle = {
  /** The person's name as the list's line carries it, whole; null without one. */
  readonly person: string | null;
  /** The name of the experience, or the sentence of a retired one. */
  readonly experience: string;
  /** « Camille, Accueil PMS, 22 septembre 2026, 14:02 » - the last crumb. */
  readonly crumb: string;
};

export const RETIRED_EXPERIENCE_NAME = 'Expérience retirée';

export function headTitleOf(session: SessionDetail['session']): HeadTitle {
  const experience = session.experienceName ?? RETIRED_EXPERIENCE_NAME;
  const person = session.personName?.value ?? null;
  const when = sessionDate(session.startedAt);
  return {
    person,
    experience,
    crumb: `${person === null ? '' : `${person}, `}${experience}, ${when}`,
  };
}

/** Where the pill of the line leads, inside the tab Parcours. */
export type HeadNoyauTarget = 'determination' | 'trial';

export type HeadNoyauLine = {
  readonly kind: 'retained' | 'trial';
  /** « Noyau retenu » or « Lu à blanc ». */
  readonly lead: string;
  readonly code: NoyauCode;
  /** The deposited name, whole - « Producteur Réinventeur ». */
  readonly name: string;
  /** « la carte prise » or « le principal »; null on a trial, and when the line of the gap does not read. */
  readonly how: string | null;
  /** « 56 contre 42 »; null when the notes are not there to read. */
  readonly figures: string | null;
  readonly target: HeadNoyauTarget;
};

export const HEAD_RETAINED_LEAD = 'Noyau retenu';
export const HEAD_TRIAL_LEAD = 'Lu à blanc';

/** What each state of the line of the gap says retained the noyau (noyau.md §3.8, §9.1). */
const HOW_OF_STATE: Readonly<Record<NoyauGapLineState, string>> = {
  decides: 'la carte prise',
  'before-threshold': 'la carte prise',
  'no-effect': 'le principal',
  'second-eliminated': 'le principal',
};

export function noyauFiguresSentence(shown: number, other: number): string {
  return `${shown} contre ${other}`;
}

function scoreOf(determination: NoyauDetermination, code: NoyauCode | null): number | null {
  if (code === null) return null;
  return determination.scores.find((entry) => entry.noyau === code)?.score ?? null;
}

/**
 * Whether the trials of the session are worth asking for: only a line that
 * retained no noyau and that shows the principal of a trial reads them.
 */
export function headNeedsTrials(session: SessionDetail['session']): boolean {
  return (session.noyauCodes?.retained ?? null) === null && session.noyauTrial !== null;
}

function retainedLine(detail: SessionDetail, retained: NoyauCode): HeadNoyauLine {
  const noyau = detail.noyau;
  const determination = noyau?.determination ?? null;
  const first = noyau?.choices.find((choice) => choice.mode === 'first-choice');
  const gap =
    noyau === null || noyau === undefined || first === undefined
      ? null
      : noyauGapLineOf(noyau, first);

  // The note of the noyau SHOWN against the other of the first choice: the
  // principal against the second, or the second - taken by the card - against
  // the principal.
  let figures: string | null = null;
  if (determination !== null) {
    const { principal, second } = determination;
    const other = retained === principal ? second : retained === second ? principal : null;
    const shown = scoreOf(determination, retained);
    const against = scoreOf(determination, other);
    if (shown !== null && against !== null) figures = noyauFiguresSentence(shown, against);
  }

  return {
    kind: 'retained',
    lead: HEAD_RETAINED_LEAD,
    code: retained,
    name: noyauOf(retained).name,
    how: gap === null ? null : HOW_OF_STATE[gap.state],
    figures,
    target: 'determination',
  };
}

function trialLine(
  code: NoyauCode,
  trials: readonly Pick<NoyauTrialSummary, 'determination'>[],
): HeadNoyauLine {
  // The list is newest first, and the column of the list keeps the newest
  // RENDERED one: the first rendered trial whose principal is that code.
  const found = trials.find(
    (trial) => trial.determination.state === 'rendered' && trial.determination.principal === code,
  );
  let figures: string | null = null;
  if (found !== undefined) {
    const { determination } = found;
    const shown = scoreOf(determination, determination.principal);
    const against = scoreOf(determination, determination.second);
    if (shown !== null && against !== null) figures = noyauFiguresSentence(shown, against);
  }
  return {
    kind: 'trial',
    lead: HEAD_TRIAL_LEAD,
    code,
    name: noyauOf(code).name,
    how: null,
    figures,
    target: 'trial',
  };
}

/**
 * The line « Noyau retenu » under the title, or « Lu à blanc » when no noyau
 * was retained and a trial rendered one; null with neither. A retained noyau
 * always wins over a trial, as in the column of the list.
 */
export function headNoyauLine(
  detail: SessionDetail,
  trials: readonly Pick<NoyauTrialSummary, 'determination'>[] = [],
): HeadNoyauLine | null {
  const { session } = detail;
  const retained = session.noyauCodes?.retained ?? null;
  if (retained !== null) return retainedLine(detail, retained);
  if (session.noyauTrial !== null) return trialLine(session.noyauTrial, trials);
  return null;
}

/** « le principal, 56 contre 42 » - what follows the name, or null when there is nothing to add. */
export function headNoyauDetail(line: HeadNoyauLine): string | null {
  const parts = [line.how, line.figures].filter((part): part is string => part !== null);
  if (parts.length === 0) return null;
  // « le principal, 56 contre 42 »: the cause and the figures are one clause.
  return parts.join(', ');
}
